import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import Reveal from "./Reveal";
import PortfolioLightbox from "./PortfolioLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_ITEMS = [
  { id: "d1", url: "https://images.unsplash.com/photo-1452573992436-6d508f200b30?w=1200&q=85&fm=jpg&fit=crop", category: "Ciclismo de Estrada", description: "Ciclistas em ação", span: "tall" },
  { id: "d2", url: "https://images.unsplash.com/photo-1601625193660-86f2807b024b?w=800&q=85&fm=jpg&fit=crop", category: "Mountain Bike", description: "BMX rider", span: "normal" },
  { id: "d3", url: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=85&fm=jpg&fit=crop", category: "Trail Running & Atletismo", description: "Trail runner", span: "normal" },
  { id: "d4", url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=85&fm=jpg&fit=crop", category: "Mountain Bike", description: "Mountain bike dirt", span: "wide" },
  { id: "d5", url: "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=800&q=85&fm=jpg&fit=crop", category: "Ciclismo de Estrada", description: "Road cycling", span: "normal" },
  { id: "d6", url: "https://images.unsplash.com/photo-1533107862482-0e6974b06ec4?w=800&q=85&fm=jpg&fit=crop", category: "Trail Running & Atletismo", description: "Athletics track", span: "normal" },
  { id: "d7", url: "https://images.unsplash.com/photo-1544298621-35a764317116?w=800&q=85&fm=jpg&fit=crop", category: "Retratos Artísticos", description: "Retrato artístico", span: "tall" },
  { id: "d8", url: "https://images.unsplash.com/photo-1461897104016-0b3b00cc81ee?w=800&q=85&fm=jpg&fit=crop", category: "Ciclismo de Estrada", description: "Ciclismo velódromo", span: "normal" },
];

const spanClass = (span) => {
  if (span === "tall") return "row-span-2";
  if (span === "wide") return "md:col-span-2";
  return "";
};

const PortfolioGrid = ({ filter = "Todos" }) => {
  const [items, setItems] = useState([]);
  const [fadeKey, setFadeKey] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`)
      .then((r) => setItems(Array.isArray(r.data) && r.data.length ? r.data : DEMO_ITEMS))
      .catch(() => setItems(DEMO_ITEMS));
  }, []);

  // Re-trigger fade transition whenever filter changes
  useEffect(() => {
    setFadeKey((k) => k + 1);
  }, [filter]);

  const visible = filter === "Todos" ? items : items.filter((i) => i.category === filter);

  return (
    <>
    {lightboxIndex !== null && (
      <PortfolioLightbox
        items={visible}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((i) => Math.max(0, i - 1))}
        onNext={() => setLightboxIndex((i) => Math.min(visible.length - 1, i + 1))}
      />
    )}
    <section
      id="portfolio"
      className="bg-black py-16 md:py-24 border-t border-white/10"
      data-testid="portfolio-section"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="divider-red" />
                <span className="font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase">02 — Portfólio</span>
              </div>
              <h2 className="font-heading text-white text-6xl md:text-8xl uppercase leading-[0.9]" data-testid="portfolio-title">
                Trabalhos<br />Recentes
              </h2>
            </div>
            <div className="font-heading text-white/55 tracking-[0.3em] uppercase text-xs">
              {visible.length} {visible.length === 1 ? "Imagem" : "Imagens"}
            </div>
          </div>
        </Reveal>

        <div
          key={fadeKey}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px] md:auto-rows-[240px] gap-3 fade-in"
          data-testid="portfolio-grid"
        >
          {visible.map((it, idx) => (
            <div
              key={it.id}
              className={`port-card cursor-pointer ${spanClass(it.span)}`}
              data-testid={`portfolio-item-${idx}`}
              onClick={() => setLightboxIndex(idx)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setLightboxIndex(idx)}
              aria-label={`Ver foto: ${it.description || it.category}`}
            >
              <img src={it.url} alt={it.description || it.category} loading="lazy" />
              <span className="port-cat">{it.category}</span>
              <div className="port-overlay">
                <Plus size={48} strokeWidth={1.4} />
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="col-span-full text-white/50 text-center py-16 font-heading tracking-[0.3em] uppercase text-sm" data-testid="portfolio-empty">
              Sem trabalhos nesta categoria.
            </div>
          )}
        </div>
      </div>
    </section>
    </>
  );
};

export default PortfolioGrid;
